import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  IconButton,
  Slider,
  Typography,
  Tooltip,
  Paper
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  VolumeUp as VolumeIcon
} from '@mui/icons-material';

const AudioPlayer = ({ 
  audioUrl, 
  onPlay, 
  onPause, 
  onStop, 
  onError,
  callId,
  isPlaying,
  compact = false 
}) => {
  const audioRef = useRef(null);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !audioUrl) return;

    const handleLoadedData = () => {
      setDuration(audio.duration);
      setIsLoading(false);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      setCurrentTime(0);
      onStop?.();
    };

    const handleError = (e) => {
      setIsLoading(false);
      onError?.(e);
    };

    audio.addEventListener('loadeddata', handleLoadedData);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('loadeddata', handleLoadedData);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, [audioUrl, onStop, onError]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.play().catch(onError);
    } else {
      audio.pause();
    }
  }, [isPlaying, onError]);

  const handlePlayPause = () => {
    if (isPlaying) {
      onPause?.();
    } else {
      onPlay?.();
    }
  };

  const handleStop = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = 0;
      setCurrentTime(0);
    }
    onStop?.();
  };

  const handleSeek = (event, newValue) => {
    const audio = audioRef.current;
    if (audio && duration > 0) {
      const seekTime = (newValue / 100) * duration;
      audio.currentTime = seekTime;
      setCurrentTime(seekTime);
    }
  };

  const handleVolumeChange = (event, newValue) => {
    const audio = audioRef.current;
    const newVolume = newValue / 100;
    setVolume(newVolume);
    if (audio) {
      audio.volume = newVolume;
    }
  };

  const formatTime = (time) => {
    if (!time || !isFinite(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  if (compact) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <audio ref={audioRef} src={audioUrl} preload="metadata" />
        <Tooltip title={isPlaying ? "Pause" : "Play"}>
          <IconButton 
            size="small" 
            onClick={handlePlayPause}
            disabled={isLoading}
            color={isPlaying ? "secondary" : "default"}
          >
            {isPlaying ? <PauseIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Stop">
          <IconButton 
            size="small" 
            onClick={handleStop}
            disabled={!isPlaying && currentTime === 0}
          >
            <StopIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
      <audio ref={audioRef} src={audioUrl} preload="metadata" />
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={isPlaying ? "Pause" : "Play"}>
            <IconButton 
              onClick={handlePlayPause}
              disabled={isLoading}
              color={isPlaying ? "secondary" : "primary"}
              size="large"
            >
              {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Stop">
            <IconButton 
              onClick={handleStop}
              disabled={!isPlaying && currentTime === 0}
            >
              <StopIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ flex: 1, mx: 2 }}>
          <Slider
            value={progress}
            onChange={handleSeek}
            disabled={isLoading || duration === 0}
            sx={{
              color: 'primary.main',
              '& .MuiSlider-thumb': {
                width: 16,
                height: 16,
              },
            }}
          />
        </Box>

        <Typography variant="body2" sx={{ minWidth: '80px', textAlign: 'center' }}>
          {formatTime(currentTime)} / {formatTime(duration)}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
        <VolumeIcon fontSize="small" />
        <Slider
          value={volume * 100}
          onChange={handleVolumeChange}
          sx={{ width: 100 }}
          size="small"
        />
        <Typography variant="caption" color="textSecondary">
          Call Recording for {callId}
        </Typography>
      </Box>
    </Paper>
  );
};

export default AudioPlayer; 